/* eslint-disable @typescript-eslint/no-non-null-assertion */
/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import { useAppDispatch } from "@src/config/store";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { getUser, sendOTPToken } from "./ducks/operations";
import { IRootState } from "@src/config/reducers";
import { useSelector } from "react-redux";
import {
  AppAlert,
  exposeCaptchaHeaders,
  getRecaptchaToken,
  IBrowserError,
  IErrorMessage,
  LaddaButton,
  RECAPTCHA_MAX_RETRIES,
  RECAPTCHA_RETRY_DELAY_MS,
  RECAPTCHA_SCRIPT_LOAD_RETRIES,
  RecaptchaTokenAction,
  useCaptcha,
} from "@advapacs/common";
import OtpInput from "react18-input-otp";
import axios from "axios";
import { UnauthenticatedActions } from "./ducks/slices";
import { useNavigate } from "react-router-dom";
import { formatRegionUrl } from "@src/utils/utils";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { getEula } from "@src/pages/authenticated/userLicense/ducks/operators";
import { prodModeLog, reCaptchaDelay } from "@advapacs/common/src/utils/utils";
import {
  GOOGLE_RECAPTCHA_ENTERPRISE,
  TURNSTILE_SITE_KEY,
} from "@src/config/constants";

interface IVerifyStep {
  editOtpCode?: () => void;
  sms?: boolean;
}

export const VerifyStep: FC<IVerifyStep> = ({ editOtpCode, sms }) => {
  const dispatch = useAppDispatch();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const executeRecaptchaRef = useRef(executeRecaptcha);
  const captchaType = useCaptcha();
  const turnstileRef = useRef<HTMLDivElement>(null);
  const tempTurnstileAction = useRef({
    action: "",
    code: "",
    token: "",
    sms: false,
  });
  const [turnstileError, setTurnstileError] = useState("");
  const { loading, resendOTPResponse, configuration, errorMessage } =
    useSelector((state: IRootState) => state.unAuthenticatedReducer);
  const [code, setCode] = useState("");
  const [currentCount, setCurrentCount] = useState(0);
  const timerCount = useRef(60);
  const timer = useRef<NodeJS.Timeout>(null);
  const [enabledResend, setEnabledResend] = useState(true);
  const [exceedError, setExceedError] = useState(false);
  const [browserError, setBrowserError] = useState<IBrowserError | null>(null);
  const [inCorrectOTP, setInCorrectOTP] = useState(false);
  const [invalidRecaptcha, setInvalidRecaptcha] = useState(false);
  const navigate = useNavigate();

  const [captchaLoading, setCaptchaLoading] = useState(false);

  const handleBrowserError = (err: IErrorMessage | string) => {
    const errMsg =
      typeof err === "string" ? err : err?.message || "Unknown error messsage";
    setBrowserError({ message: errMsg });
  };

  const generateRecaptchaToken = async (
    action: RecaptchaTokenAction,
    retries = RECAPTCHA_MAX_RETRIES
  ): Promise<string> => {
    setBrowserError(null); // reset error message when starting generate token.
    setCaptchaLoading(true);
    const currentExecuteRecaptcha = executeRecaptchaRef.current; // alway fresh

    // Script not loaded yet — retry until ready, then give up
    if (!currentExecuteRecaptcha) {
      if (retries > 0) {
        console.warn("Re-trigger waiting gRecaptcha loaded.");
        await reCaptchaDelay(RECAPTCHA_SCRIPT_LOAD_RETRIES);
        return generateRecaptchaToken(action, retries - 1);
      }
      setCaptchaLoading(false);
      return Promise.reject(new Error("reCAPTCHA script not loaded yet."));
    }
    try {
      return await getRecaptchaToken(
        action,
        GOOGLE_RECAPTCHA_ENTERPRISE,
        currentExecuteRecaptcha
      );
    } catch (error) {
      if (retries > 0) {
        const delayTime =
          RECAPTCHA_RETRY_DELAY_MS * (RECAPTCHA_MAX_RETRIES - retries + 1); // linear/exponential backoff
        console.warn(
          `reCAPTCHA failed. Retrying in ${
            delayTime / 1000
          }s... (${retries} retries left)`
        );
        await reCaptchaDelay(delayTime);
        return generateRecaptchaToken(action, retries - 1);
      }
      setCaptchaLoading(false);
      return Promise.reject((error as IErrorMessage)?.message || error);
    }
  };

  const validateTurnstile = () => {
    if (!turnstileRef?.current || !window.turnstile) {
      setCaptchaLoading(false);
      return false;
    }
    return true;
  };

  const handleSubmitCode = async (
    newCode: string,
    captchaToken: string,
    turnstile?: boolean
  ) => {
    axios.defaults.baseURL = formatRegionUrl(
      localStorage.getItem(`${window.location.host}.region`) as string
    ); // Always overwrite new region detected.

    try {
      await axios.post(
        `/auth/${configuration?.token}/verify`,
        {
          channel: sms ? "SMS" : "EMAIL",
          token: newCode,
        },
        {
          headers: exposeCaptchaHeaders(turnstile!, captchaToken),
        }
      );
      setCaptchaLoading(false);

      dispatch(getUser());
      dispatch(getEula());

      // clear local storage
      if (!!localStorage.getItem("newAccessId")) {
        localStorage.setItem(
          "accessId",
          localStorage.getItem("newAccessId") as string
        );
        localStorage.removeItem("newAccessId");
      }
      navigate("/Exams");
    } catch (err) {
      handleBrowserError((err as IErrorMessage)?.response?.data);
      dispatch(UnauthenticatedActions.setLoadingState()); // reset all loading state.
      setCaptchaLoading(false);
      return;
    }
  };

  const handleCallbackFunction = async (turnstileToken: string) => {
    switch (tempTurnstileAction.current.action) {
      case "verifyCode":
        await handleSubmitCode(
          tempTurnstileAction.current.code,
          turnstileToken,
          true
        );
        break;
      case "handleCountDown":
        dispatch(
          sendOTPToken(
            tempTurnstileAction.current.token,
            tempTurnstileAction.current.sms ? "SMS" : "EMAIL",
            turnstileToken,
            true
          )
        );
        break;
      default:
        break;
    }

    prodModeLog("Finish function " + tempTurnstileAction.current.action);
    // reset after callback called.
    window.turnstile?.reset(turnstileRef.current!); // reset
    tempTurnstileAction.current.action = "";
    tempTurnstileAction.current.code = "";
    tempTurnstileAction.current.token = "";
    tempTurnstileAction.current.sms = false;
    setCaptchaLoading(false);
  };

  const submit = async () => {
    try {
      dispatch(UnauthenticatedActions.loginStart());

      if (captchaType?.provider === "turnstile") {
        if (!validateTurnstile()) {
          setTurnstileError("Turnstile not loaded in client Side.");
          return;
        }
        // handle turnstile.
        setTurnstileError(""); // reset error.
        // turnstile flow.
        window.turnstile?.reset(turnstileRef.current!); // reset before start
        tempTurnstileAction.current.action = "verifyCode"; //
        tempTurnstileAction.current.code = code;
        // trigger invisible challenge
        setCaptchaLoading(true); // trigger loading state.
        window.turnstile?.execute(turnstileRef.current!, {
          action: RecaptchaTokenAction.PATIENT_LOGIN_VERIFY_OTP,
          async: false,
        });

        prodModeLog(
          "Trigger execute generate function from turnstile, (verifyCode)"
        );
      } else {
        const recaptchaToken = await generateRecaptchaToken(
          RecaptchaTokenAction.PATIENT_LOGIN_VERIFY_OTP
        );
        if (!recaptchaToken) throw Error("Missing Recaptcha Token");
        await handleSubmitCode(code, recaptchaToken, false);
      }
    } catch (err) {
      dispatch(
        UnauthenticatedActions.loginFail({
          err: (err as IErrorMessage)?.response?.data,
          pathname: "/",
        })
      );
    } finally {
      // @ts-ignore
      clearInterval(timer?.current);
      timerCount.current = 0;
    }
  };

  useEffect(() => {
    executeRecaptchaRef.current = executeRecaptcha;
  }, [executeRecaptcha]);

  useEffect(() => {
    if (errorMessage) {
      switch (errorMessage.code) {
        // Expire and invalid OTP.
        case "1000":
        case "1007":
          setInCorrectOTP(true);
          setExceedError(false);
          setInvalidRecaptcha(false);
          break;
        case "429":
          setInCorrectOTP(false);
          setInvalidRecaptcha(false);
          if (errorMessage?.type === "RateLimitedException") {
            setExceedError(true);
            setEnabledResend(true);
            // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
            clearInterval(timer?.current!);
          }
          break;
        case "1050":
        case "1051":
          setInvalidRecaptcha(true);
          setInCorrectOTP(false);
          setExceedError(false);
          break;
        default:
          setInCorrectOTP(false);
          setExceedError(false);
          setInvalidRecaptcha(false);
          break;
      }
    }
  }, [errorMessage]);

  const handleCountDown = async (calling = false) => {
    if (!enabledResend) return;

    if (calling) {
      if (captchaType.provider === "turnstile") {
        if (!validateTurnstile()) {
          setTurnstileError("Turnstile not loaded in client Side.");
          return;
        }
        // handle turnstile.
        setTurnstileError(""); // reset error.
        // turnstile flow.
        window.turnstile?.reset(turnstileRef.current!); // reset before start
        tempTurnstileAction.current.action = "handleCountDown"; //
        // @ts-ignore
        tempTurnstileAction.current.token = configuration?.token;
        // @ts-ignore
        tempTurnstileAction.current.sms = sms;
        // trigger invisible challenge
        setCaptchaLoading(true); // trigger loading state.
        window.turnstile?.execute(turnstileRef.current!, {
          action: RecaptchaTokenAction.PATIENT_LOGIN_SEND_OTP,
          async: false,
        });

        prodModeLog(
          "Trigger execute generate function from turnstile, (handleCountDown)"
        );
      } else {
        try {
          // avoid exception when trying generate recaptcha without ff.
          const recaptchaToken = await generateRecaptchaToken(
            RecaptchaTokenAction.PATIENT_LOGIN_SEND_OTP
          );
          if (!recaptchaToken) throw Error("Missing Recaptcha Token");
          dispatch(
            sendOTPToken(
              configuration?.token,
              sms ? "SMS" : "EMAIL",
              recaptchaToken
            )
          );
        } catch (err) {
          handleBrowserError((err as IErrorMessage)?.response?.data);
        }
      }
    }

    setEnabledResend(false);
    // @ts-ignore
    timer.current = setInterval(() => {
      if (timerCount.current > 0) {
        timerCount.current = timerCount.current - 1;
        setCurrentCount(timerCount.current);
      } else {
        // @ts-ignore
        clearInterval(timer.current);
        setEnabledResend(true);
        setCurrentCount(0);
        timerCount.current = 60;
      }
    }, 1000);
  };

  useEffect(() => {
    handleCountDown();
    return () => {
      // @ts-ignore
      clearInterval(timer?.current);
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emailOrSMSLabel = useMemo(() => {
    if (!resendOTPResponse) return "";
    return sms ? resendOTPResponse?.mobilePhone : resendOTPResponse?.email;
  }, [resendOTPResponse, sms]);

  const getEmailTruced = useMemo(() => {
    // @ts-ignore
    const [prefix, suffix] = emailOrSMSLabel.split("@");
    if (prefix && suffix && prefix.length > 6 && suffix.length > 30) {
      return `${prefix.slice(prefix.length - 6)}@***${suffix.slice(
        suffix.length - 30
      )}`;
    }

    return emailOrSMSLabel;
  }, [emailOrSMSLabel]);

  // recaptcha effect.
  useEffect(() => {
    if (captchaType?.provider === "turnstile") {
      /**
         * The Turnstile token is one-time use — it expires quickly (typically within 2 minutes).
          After sending it to your backend (or after any failed verification), you’ll usually need to refresh / reset the widget to generate a new token.
         */

      const interval = setInterval(() => {
        if (
          typeof window !== "undefined" &&
          window?.turnstile &&
          turnstileRef?.current
        ) {
          // eslint-disable-next-line no-console
          console.log("call interval !!!");
          clearInterval(interval);

          if (turnstileRef.current.hasChildNodes()) {
            // avoid call re-render multiple times
            console.warn(
              "Cloudflare Turnstile has already been rendered. Prevent !!!"
            );
            return;
          }
          window.turnstile?.render(turnstileRef.current, {
            sitekey: TURNSTILE_SITE_KEY,
            size: "invisible",
            callback: (turnstileToken: string) => {
              if (tempTurnstileAction.current.action !== "") {
                prodModeLog(
                  "Callback token from turnstile, will trigger function verifyCode"
                );
                handleCallbackFunction(turnstileToken);
              } else {
                prodModeLog("No action with callback function !!!");
              }
            },
            "expired-callback": () => {
              setCaptchaLoading(false);
              window.turnstile?.reset(turnstileRef.current!);
            },
            "error-callback": (turnstileErrorCode) => {
              // https://developers.cloudflare.com/turnstile/troubleshooting/client-side-errors/error-codes/
              setCaptchaLoading(false);
              // eslint-disable-next-line no-console
              console.log({ turnstileErrorCode });
              // window.turnstile?.reset(turnstileRef.current!); // it will trigger retries when error callback.

              setTurnstileError(
                `Cloudflare Turnstile get error with code: ${turnstileErrorCode}`
              );
            },
          });
        }
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captchaType]);

  return (
    <>
      <div ref={turnstileRef} />
      <form autoComplete="off">
        <div className="w-100">
          <div className="col-lg-12 col-xs-12">
            <h3 className=" font-weight">{configuration?.name}</h3>
            <h5 className="m-b-sm">Patient Portal Login</h5>
          </div>

          <div className="col-lg-12">
            <p className="d-inline">
              {`A 6-digit code has been sent to ${getEmailTruced}. Please enter it within the next 5 minutes.`}
            </p>
          </div>

          <div className="col-lg-12 m-b-lg">
            <p
              onClick={() => {
                if (editOtpCode) editOtpCode();
              }}
              className="text-primary font-weight cursor-pointer"
            >
              Change OTP Method
            </p>
          </div>

          <div className="col-lg-12 text-left">
            <p className="label m-b-xxs w-100 text-center">Verification Code</p>
            <div className="otp-form">
              <OtpInput
                shouldAutoFocus
                isInputNum
                className="form-control verify-code"
                value={code}
                onChange={(value: string) => {
                  setInCorrectOTP(false);
                  setCode(value);
                }}
                numInputs={6}
              />
              {inCorrectOTP && (
                <AppAlert.SimpleAlert
                  variant="danger"
                  className="m-t-xs m-b-none expired-error "
                  message={"OTP is incorrect or expired. Please try again."}
                />
              )}
            </div>
          </div>

          <div className="col-lg-12 m-t-lg w-100">
            <LaddaButton
              loading={loading || captchaLoading}
              type="submit"
              className="btn btn-primary btn-verify"
              disabled={!code || code.length < 6}
              btnName={"Submit"}
              onClickHandler={submit}
            />
          </div>

          <div className="col-lg-12 m-t-md otp-form">
            {exceedError && (
              <AppAlert.SimpleAlert
                variant="warning"
                className="text-left expired-error"
                message={
                  "Unable to resend OTP due to too many attempts. Please try again later."
                }
              />
            )}

            <p
              onClick={() => handleCountDown(true)}
              className={`font-weight cursor-pointer ${
                !enabledResend
                  ? "pp-text-gray cursor-not-allowed"
                  : "text-primary"
              }`}
            >
              Resend OTP {currentCount > 0 ? `(in ${currentCount}s)` : ""}
            </p>
          </div>
          <div className="col-lg-12 m-t-md otp-form">
            {invalidRecaptcha && errorMessage && (
              <AppAlert.SimpleAlert
                variant="warning"
                className="text-left expired-error"
                message={errorMessage.message}
              />
            )}
            {turnstileError && (
              <AppAlert.SimpleAlert
                variant="warning"
                className="text-left"
                message={turnstileError}
              />
            )}
            {browserError && (
              <AppAlert.SimpleAlert
                variant="warning"
                className="text-left "
                message={browserError?.message}
              />
            )}
          </div>
        </div>
      </form>
    </>
  );
};
